import { useProjectParams } from '@hooks/useProjectParams';
import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { CustomCollapse } from 'components/CustomCollapse';
import { KebabTreeContract } from 'components/KebabTreeContract';
import { KebabTreeWorkOrder } from 'components/KebabTreeWorkOrder';
import { KebabTreeWorkOrderChange } from 'components/KebabTreeWorkOrderChange';
import type { WorkOrderChangeNavData } from 'gql/graphql';
import { ProjectMethodologyEnum, WorkOrderVorTypeEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { ModalsCreateWorkOrder } from 'pages/Project/UerProject/Contract/CreateWorkOrder/ModalsCreateWorkOrder';
import { useCreateWorkOrder } from 'pages/Project/UerProject/Contract/CreateWorkOrder/useCreateWorkOrder';
import { methodologyMapReverse } from 'types/methodology';
import { cnMixSpace, IconAlert } from 'uikit';
import { formatDateToLocale } from 'utils';
import { workOrderChangeDescriptionNumberAndDate } from 'utils/descriptionNumberAndDate';
import { getLabelWorkOrder } from 'utils/getLabelWorkOrder';
import { StopPropagationEnum, stopPropagationHelper } from 'utils/stopPropagationHelper';

import { F as EmptyFn } from 'ramda';
import type { BaseSyntheticEvent } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ProjectTreeLeaf } from './ProjectTreeLeaf';
import { useCheckActiveTreeItem } from './useCheckActiveTreeItem';

type TContractTree = {
    getContractNavItemUrl: ({
        baseUrl,
        itemId,
        navLinkId,
    }: {
        baseUrl: string;
        itemId: number;
        navLinkId: string;
    }) => string;
};

export const ContractTree = ({ getContractNavItemUrl }: TContractTree) => {
    const { contract: contractRules, workOrderChangeUer: workOrderChangeUerRules } = useAccess();
    const { id, methodology } = useProjectParams();
    const navigate = useNavigate();
    const { state }: { state: { contractId?: number; refreshTree?: boolean } } = useLocation();

    const { projectData, refreshProject } = useProjectData(id, false);

    const { hasNavigation, serviceInfo, contractNavigationData } = useMemo(
        () => ({
            hasNavigation: !!projectData?.contractNavigation,
            serviceInfo: projectData?.contractNavigation,
            contractNavigationData: projectData?.contractNavigation?.data,
        }),
        [projectData?.contractNavigation],
    );

    const contractStatus = contractNavigationData?.contractNavData?.status;
    const contractId = projectData?.contractId;
    const statusProject = projectData?.status;

    const isAvailableWorkOrder = !!contractNavigationData?.workOrderNavs?.length;

    const handleContractClick = useCallback(() => {
        if (contractId) {
            navigate(`/projects/${methodology}/${id}/contract`);
        } else {
            navigate(`/projects/${methodology}/${id}/contract/upload`);
        }
    }, [contractId, navigate, methodology, id]);

    const handleWorkOrderClick = useCallback(
        (orderId) => (event: BaseSyntheticEvent) => {
            const propagate = stopPropagationHelper({ event });

            if (propagate) {
                navigate(`/projects/${methodology}/${id}/order/${orderId}`);
            }
        },
        [id, methodology, navigate],
    );

    useEffect(() => {
        if (state?.contractId || state?.refreshTree) {
            refreshProject();
        }
    }, [state?.contractId, state?.refreshTree, refreshProject]);

    const {
        orderRules,
        isContractModalActive,
        isUnreadyModalActive,
        unreadyUerMsg,
        CreateWorkOrderConfirmationDialog,
        modalContractConfirmHandler,
        modalUnreadyConfirmHandler,
        uploadClickHandler,
        modalCancelHandler,
    } = useCreateWorkOrder(id, contractNavigationData?.contractNavData);

    const checkEntityIsActive = useCheckActiveTreeItem();

    if (!hasNavigation) {
        return null;
    }

    return (
        <>
            {!serviceInfo?.result && (
                <ComponentWithTooltip tooltipText={serviceInfo?.message}>
                    <ProjectTreeLeaf
                        label={
                            <span className="serviceMsgNode">
                                <IconAlert view="alert" className={cnMixSpace({ mR: '2xs' })} />
                                Договор
                            </span>
                        }
                        horizontalSpace="m"
                        onClick={EmptyFn}
                        boldStyle
                    />
                </ComponentWithTooltip>
            )}

            {contractNavigationData && (
                <ProjectTreeLeaf
                    label={contractNavigationData?.contractNavData?.name}
                    horizontalSpace="m"
                    onClick={handleContractClick}
                    rightSide={
                        contractId ? (
                            <>
                                <KebabTreeContract
                                    projectId={id}
                                    methodology={methodology}
                                    orderRules={orderRules}
                                    contractRules={contractRules}
                                    onCreateWorkOrder={uploadClickHandler}
                                    statusContract={contractStatus}
                                    statusProject={statusProject}
                                    isAvailableWorkOrder={isAvailableWorkOrder}
                                />
                                {CreateWorkOrderConfirmationDialog}
                            </>
                        ) : undefined
                    }
                    boldStyle
                    isActiveLink={checkEntityIsActive({
                        entityName: 'contract',
                        entityId: ['upload', 'edit'],
                    })}
                />
            )}

            {contractNavigationData?.contractNavItems?.map((contractItem) => {
                const baseUrl = `/projects/${methodology}/${id}`;
                const { id: itemId, navLinkId, metadata } = contractItem;

                const urlTo = getContractNavItemUrl({
                    itemId: itemId ?? metadata?.contractCalculationId,
                    baseUrl,
                    navLinkId,
                });

                const { status, isWorkOrderChangeRequired, hasInProgress } = contractItem?.metadata ?? {};

                return (
                    <ProjectTreeLeaf
                        key={contractItem.name}
                        label={contractItem.name}
                        to={urlTo}
                        rightSide={
                            itemId && (
                                <KebabTreeWorkOrder
                                    itemId={itemId}
                                    contractId={contractId}
                                    statusWorkOrder={status}
                                    statusContract={contractStatus}
                                    kebabBadgeSize="s"
                                    refreshProject={refreshProject}
                                    orderRules={orderRules}
                                    projectData={projectData}
                                    workOrderLabel={contractItem.name}
                                    statusProject={statusProject}
                                    isWorkOrderChangeRequired={isWorkOrderChangeRequired}
                                    hasInProgress={hasInProgress}
                                />
                            )
                        }
                        horizontalSpace="2xl"
                        isActiveLink={checkEntityIsActive({
                            entityName: getContractNavItemUrl({
                                itemId: itemId ?? metadata?.contractCalculationId,
                                baseUrl,
                                navLinkId,
                            }),
                            entityId: '',
                        })}
                    />
                );
            })}
            {contractNavigationData?.workOrderNavs?.map((order) => {
                const isActKC2Navs = !!order?.actKC2Navs?.length;

                const bimHasActKc2List =
                    methodology &&
                    methodologyMapReverse[methodology] === ProjectMethodologyEnum.Bim &&
                    order?.metadata?.hasActsKC2;

                const isActKC3Navs = !!order?.actKC3Navs?.length;
                const isWorkOrderChangesNavs = !!order?.workOrderChanges?.length;
                const isWorkOrderVorTypeXml = order?.metadata?.vorType === WorkOrderVorTypeEnum.XmlBundle;
                const { isWorkOrderChangeRequired, hasInProgress } = order?.metadata || {};

                const isWorkOrderEmpty = !isActKC2Navs && !isActKC3Navs && !isWorkOrderChangesNavs && !bimHasActKc2List;

                return (
                    <CustomCollapse
                        id={StopPropagationEnum.DEFAULT_ID}
                        key={order.id}
                        label={getLabelWorkOrder(order)}
                        level="second"
                        directionIcon="left"
                        size="s"
                        horizontalSpace="2xl"
                        emptyCollapse={isWorkOrderEmpty}
                        rightSide={
                            <KebabTreeWorkOrder
                                itemId={order.id}
                                contractId={contractId}
                                statusWorkOrder={order.status}
                                statusContract={contractStatus}
                                kebabBadgeSize="s"
                                refreshProject={refreshProject}
                                orderRules={orderRules}
                                projectData={projectData}
                                workOrderLabel={getLabelWorkOrder(order)}
                                statusProject={statusProject}
                                isActKC2Navs={isActKC2Navs}
                                isWorkOrderVorTypeXml={isWorkOrderVorTypeXml}
                                isWorkOrderChangeRequired={isWorkOrderChangeRequired}
                                hasInProgress={hasInProgress}
                            />
                        }
                        onClick={handleWorkOrderClick(order.id)}
                        isActiveLink={checkEntityIsActive({
                            entityName: 'order',
                            entityId: order.id,
                        })}
                        saveCollapseStateKey={`workOrderList_${order.id}`}
                    >
                        {(isActKC2Navs || bimHasActKc2List) && (
                            <ProjectTreeLeaf
                                key={`KC2List${order.id}`}
                                label="Акты КС-2"
                                to={`/projects/${methodology}/${id}/order/${order.id}/actsKC2List`}
                                horizontalSpace="4xl"
                                isActiveLink={checkEntityIsActive({
                                    entityName: 'order',
                                    entityId: `${order.id}/actsKC2List`,
                                })}
                            />
                        )}
                        {isWorkOrderChangesNavs && (
                            <CustomCollapse
                                label="Изм. к НЗ"
                                directionIcon="left"
                                size="s"
                                level="third"
                                saveCollapseStateKey={`workOrderChangeList_${order.id}`}
                            >
                                {order?.workOrderChanges?.map((workOrderChange: WorkOrderChangeNavData, i: number) => (
                                    <ProjectTreeLeaf
                                        id={StopPropagationEnum.DEFAULT_ID}
                                        key={`workOrderChange${workOrderChange.id}${workOrderChange.status}${i}`}
                                        label={`Изм.${workOrderChangeDescriptionNumberAndDate(
                                            workOrderChange.workOrderChangeNumber,
                                            workOrderChange?.signingDate,
                                            workOrderChange.workOrderNumber,
                                        )}`}
                                        to={`/projects/${methodology}/${id}/orderChange/${workOrderChange.id}`}
                                        rightSide={
                                            <KebabTreeWorkOrderChange
                                                status={workOrderChange.status}
                                                workOrderChangeId={workOrderChange.id}
                                                refreshProject={refreshProject}
                                                projectData={projectData}
                                                statusContract={contractStatus}
                                                statusProject={statusProject}
                                                methodology={methodology}
                                                workOrderChangeUerRules={workOrderChangeUerRules}
                                                workOrderId={order.id}
                                                label={`Изм.№ ${workOrderChange.workOrderChangeNumber} ${
                                                    workOrderChange.signingDate
                                                        ? `от ${formatDateToLocale(workOrderChange.signingDate)}`
                                                        : ''
                                                } `}
                                            />
                                        }
                                        horizontalSpace="5xl"
                                        isActiveLink={checkEntityIsActive({
                                            entityName: 'orderChange',
                                            entityId: workOrderChange.id,
                                        })}
                                    />
                                ))}
                            </CustomCollapse>
                        )}
                    </CustomCollapse>
                );
            })}
            <ModalsCreateWorkOrder
                isContractModalActive={isContractModalActive}
                isUnreadyModalActive={isUnreadyModalActive}
                unreadyUerMsg={unreadyUerMsg}
                modalCancelHandler={modalCancelHandler}
                modalContractConfirmHandler={modalContractConfirmHandler}
                modalUnreadyConfirmHandler={modalUnreadyConfirmHandler}
            />
        </>
    );
};

ContractTree.displayName = 'ContractTree';
